import h from 'hyperscript';

import duplicateViewIcon from '@disy/cadenza-icons/duplicate-view.svg';
import editIcon from '@disy/cadenza-icons/edit.svg';
import exportIcon from '@disy/cadenza-icons/export.svg';
import infoIcon from '@disy/cadenza-icons/info.svg';
import maximizeIcon from '@disy/cadenza-icons/maximize.svg';
import minimizeIcon from '@disy/cadenza-icons/minimize.svg';
import moreIcon from '@disy/cadenza-icons/more.svg';
import moveIcon from '@disy/cadenza-icons/move.svg';
import removeViewIcon from '@disy/cadenza-icons/trash.svg';
import { cadenzaUrl } from '@disy/lib.backend/cadenza-url';
import { addStyleClass } from '@disy/lib/add-style-class';
import { requireNonNullable } from '@disy/lib/custom-error';
import { logger } from '@disy/lib/logging';
import { cadenzaSessionStorage } from '@disy/lib/storage';
import { timeout } from '@disy/lib/timeout';
import { createButton, createIconButton } from '@disy/ui/button';
import { type Icon, icon } from '@disy/ui/icon';
import { MENU_ITEM_SEPARATOR, type MenuContentElement, type MenuItemGroup, setupMenuButton } from '@disy/ui/menu';
import { closeAllPopups, setupPopupButton } from '@disy/ui/popup';
import { vr } from '@disy/ui/separator';
import { getSetting } from '@disy/workbook.lib/settings';

import { confirm } from 'cadenza/alert';
import type { RepositoryDto } from 'cadenza/api-client/dto/repository-dto';
import { createWorkbookUrl } from 'cadenza/api-client/workbook-api';
import { Command } from 'cadenza/api-client/workbook-commands';
import { isFeatureAvailable } from 'cadenza/features';
import { isEmbeddedContent } from 'cadenza/integration/cadenza-api-util';
import { isDataViewOnDemand, isWorksheetOnDemand } from 'cadenza/map/on-demand/on-demand-map.state';
import { getStore } from 'cadenza/store/cadenza-store';
import { navigateWithinWorkbook } from 'cadenza/utils/confirm-navigation-util';
import { getActiveExportActions } from 'cadenza/workbook/actions/export-actions';
import i18nWorksheetDialog from 'cadenza/workbook/add-worksheet-dialog/add-worksheet-dialog.properties';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import { ViewType } from 'cadenza/workbook/api/view-type';
import type { WorksheetId } from 'cadenza/workbook/api/worksheet-id';
import { confirmSaveWorkbookBeforeAction } from 'cadenza/workbook/confirm-save-and-execute-dialog/confirm-save-and-execute-dialog';
import { checkDataViewPreconditions } from 'cadenza/workbook/data-view/data-view-utils';
import { shouldShowAttributeSummary } from 'cadenza/workbook/map/layer-info/layer-info-utils';
import { CommonViewSettingsKey } from 'cadenza/workbook/model/common-view-settings-key.gen';
import { LayerSettingsKey } from 'cadenza/workbook/model/layer-settings-key.gen';
import { MapViewSettingsKey } from 'cadenza/workbook/model/map-view-settings-key.gen';
import { getDataViewConnections } from 'cadenza/workbook/state/workbook-data-view-connections-selectors.state';
import { isOverviewMap } from 'cadenza/workbook/state/workbook-map-view-selectors.state';
import { showPanel } from 'cadenza/workbook/state/workbook-panel-actions.state';
import { isTimelinePlayerMapView } from 'cadenza/workbook/timelines/timeline.state';
import { createSaveAsCuratedMapViewMenuItem } from 'cadenza/workbook/view/map/save-curated-map-view-dialog/save-curated-map-view-utils';
import type { TextView } from 'cadenza/workbook/view/text/text-view';
import type { View } from 'cadenza/workbook/view/view';
import { isViewConfigComplete } from 'cadenza/workbook/view/view-config-utils';
import type { UpdateableElement } from 'cadenza/workbook/view/view-filters-button/view-filters-button';
import { createViewFiltersButton } from 'cadenza/workbook/view/view-filters-button/view-filters-button';
import { PrintMapAction } from 'cadenza/workbook/view/view-header/actions/print-map-action';
import { isTitleBarVisible } from 'cadenza/workbook/view/view-header/view-toolbar-utils';
import { ViewInfo, shouldShowViewDescription } from 'cadenza/workbook/view/view-info/view-info';
import { createVisualizationPickerButton } from 'cadenza/workbook/visualisation-picker/visualization-picker';
import { addWorksheet, copyViewToWorksheet } from 'cadenza/workbook/workbook-core-actions.state';
import { executeCommand } from 'cadenza/workbook/workbook-core-reducers.state';
import {
  getActiveWorksheet,
  getActiveWorksheetId,
  getDataViewsByViewId,
  getMaximizedViewId,
  getViewById,
  getViewMetadata,
  getViews,
  getWorkbookNameAndHash,
  getWorkbookRepositoryName,
  getWorksheets,
  isCombinationChart,
  isDetailView,
  isViewLoading,
  isWorkbookSaved,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';
import { WorkbookPanelName } from 'cadenza/workbook/workbook-panel/workbook-panel-utils';
import { USER_MESSAGE_STORAGE_KEY } from 'cadenza/workbook/workbook-session-storage';
import { maximizeView, minimizeView, resetDetailView, setDetailView } from 'cadenza/workbook/workbook.state';
import { isLinearLayout } from 'cadenza/workbook/worksheet-layout';
import { getDraggableOrNonDraggableClass } from 'cadenza/workbook/worksheet/worksheet-grid/worksheet-layouting-util';

import './view-actions-toolbar-button-provider.css';
import i18n from './view-actions-toolbar-button-provider.properties';

const VIEW_COMPONENT_NAME = 'd-worksheet-view';
const VIEW_ACTIONS_TOOLBAR_COMPONENT_NAME = 'd-view-actions-toolbar';
const TYPES_WITH_NO_MINMAX_BUTTON_WHEN_NO_WORKBOOK_LAYOUT_MANAGEMENT: ViewType[] = [ViewType.OTHER, ViewType.TEXT]; // types that have never min/max button are ignored here
type ExportActionDisabledReason =
  | 'UNSAVED'
  | 'NO_DATA'
  | 'LOADING'
  | 'OUTDATED'
  | '3D_MAP_VIEW_NOT_SUPPORTED'
  | 'MAP_DISPLAY_CONDITION_NOT_FULFILLED';

export function createMaximizeButton(viewId: ViewId) {
  if (minMaxButtonAvailable(viewId)) {
    return createToolbarButton(icon(maximizeIcon), i18n('maximize'), {
      styleClass: `${VIEW_COMPONENT_NAME}--maximize`,
      onclick: () => getStore().dispatch(maximizeView(viewId)),
    });
  }
}

function minMaxButtonAvailable(viewId: ViewId) {
  if (isWorkbookFeatureAvailable(getStore().getState(), 'workbook-layout-management')) {
    return true;
  }
  const view = getViewById(getStore().getState(), viewId);
  return !TYPES_WITH_NO_MINMAX_BUTTON_WHEN_NO_WORKBOOK_LAYOUT_MANAGEMENT.includes(view.type);
}

export function createMinimizeButton(viewId: ViewId) {
  if (minMaxButtonAvailable(viewId)) {
    return createToolbarButton(icon(minimizeIcon), i18n('minimize'), {
      styleClass: `${VIEW_COMPONENT_NAME}--minimize`,
      onclick: () => getStore().dispatch(minimizeView()),
    });
  }
}

function createToolbarButton(buttonIcon: Icon, title: string, options = {}) {
  const iconButton = createIconButton(buttonIcon, title, { ...options, size: 's', variant: 'borderless' });
  iconButton.classList.add(`${VIEW_ACTIONS_TOOLBAR_COMPONENT_NAME}--button`);
  return iconButton;
}

function isTextView(view: View) {
  return view.type === ViewType.TEXT;
}

export function createViewActionsToolbarEditButton(view: View) {
  if (isTextView(view) && isWorkbookFeatureAvailable(getStore().getState(), 'workbook-view-management')) {
    return createToolbarButton(icon(editIcon), i18n('editButton'), {
      onclick: () => requireNonNullable(view.querySelector<TextView>('.d-text-view')).toggleEditMode(true),
    });
  }
}

export async function createExportButton(view: View) {
  const menu = await getExportActions(view);
  const hasExportActions = menu.length > 0;
  if (hasExportActions) {
    const exportButton = createToolbarButton(icon(exportIcon), i18n('export'), { styleClass: 'force-enabled' });
    setupMenuButton(exportButton, async () => await getExportActions(view));
    return exportButton;
  }
}

async function getExportActions(view: View): Promise<MenuContentElement[]> {
  const exportActions: MenuContentElement[] = [];
  if (
    isFeatureAvailable('CREATE_WORKBOOK_REPORT') &&
    supportsAdhocReport(view) &&
    isWorkbookFeatureAvailable(getStore().getState(), 'workbook-view-pdf-export')
  ) {
    const disabledReason = await getExportDisabledReason(view, ['UNSAVED', 'MAP_DISPLAY_CONDITION_NOT_FULFILLED']);
    exportActions.push(
      applyDisabled(
        {
          label: i18n('export.report'),
          execute: () =>
            confirmSaveWorkbookBeforeAction(!isWorkbookSaved(getStore().getState()), () => openViewReportDialog(view)),
        },
        disabledReason,
      ),
    );
  }

  if (
    isWorkbookFeatureAvailable(getStore().getState(), 'workbook-view-data-and-images-export') &&
    view.type === ViewType.MAP
  ) {
    const printMapAction = new PrintMapAction(view);
    const disabledReason = await getExportDisabledReason(view, ['UNSAVED']);
    exportActions.push(
      applyDisabled(
        {
          label: printMapAction.label,
          execute: () => printMapAction.execute(),
        },
        disabledReason,
      ),
    );
  }

  if (view.dataViews.length > 0) {
    const { dataViewId } = view.dataViews[0];
    exportActions.push(...getActiveExportActions(dataViewId, { type: 'TOOLBAR' }));
  }

  return exportActions;

  function applyDisabled(action: NonNullable<MenuContentElement>, disabledReason: string | undefined) {
    return Object.assign<NonNullable<MenuContentElement>, Partial<MenuContentElement>>(action, {
      disabled: !!disabledReason,
      tooltip: disabledReason ? i18n(`export.disabled.<${disabledReason}>`) : undefined,
    });
  }
}

function supportsAdhocReport(view: View) {
  if (view.type === ViewType.MAP) {
    return (
      !isOverviewMap(getStore().getState(), view.viewId) && !isTimelinePlayerMapView(getStore().getState(), view.viewId)
    );
  }
  return ['CHART', 'TABLE'].includes(view.type);
}

async function getExportDisabledReason(view: View, irrelevantReasons: ExportActionDisabledReason[] = []) {
  let displayConditionUnfulfilled = () => false;
  if (!irrelevantReasons.includes('MAP_DISPLAY_CONDITION_NOT_FULFILLED')) {
    const unfulfilled = !(await checkDataViewPreconditions(view.viewId)).preconditionFulfilled;
    displayConditionUnfulfilled = () => unfulfilled;
  }

  return (
    reason('UNSAVED', () => !isWorkbookSaved(getStore().getState())) ??
    reason('NO_DATA', () => hasNoData(view)) ??
    reason('LOADING', () => isViewLoading(getStore().getState(), view.viewId)) ??
    reason('OUTDATED', () => getViewMetadata(getStore().getState(), view.viewId)?.loadingState === 'OUTDATED') ??
    reason('3D_MAP_VIEW_NOT_SUPPORTED', () =>
      getSetting(view, MapViewSettingsKey.toPersistenceKey('TIMELINE_3D_ENABLED'), false),
    ) ??
    reason('MAP_DISPLAY_CONDITION_NOT_FULFILLED', displayConditionUnfulfilled)
  );

  function reason(r: ExportActionDisabledReason, stateSupplier: () => boolean) {
    if (!irrelevantReasons.includes(r) && stateSupplier()) {
      return r;
    }
  }
}

async function openViewReportDialog(view: View) {
  const { ViewReportDialog } = await import('cadenza/workbook/view/view-report/view-report-dialog');
  const dataView = view.dataViews[0];
  // Conditional chaining because a map view might not have a dataView.
  return new ViewReportDialog(view.viewId, view.type, view.name, dataView?.visualizationKey)
    .connectAndShow()
    .catch((e) => logger.error('Unable to open ViewReportDialog: ', e));
}

function hasNoData(view: View) {
  const isIncomplete =
    view.type === ViewType.MAP ?
      isMapViewIncomplete(view)
    : view.dataViews.length === 0 || !isViewConfigComplete(view.type, view.dataViews[0]);
  const hasNoResults = view.content == null || view.content.querySelector('[data-no-results="true"]') != null;
  return isIncomplete || hasNoResults;
}

function isMapViewIncomplete(view: View) {
  const viewModel = getViewById(getStore().getState(), view.viewId);
  const layers = viewModel.layerTree.map((layer) => viewModel.layerDefinitions[layer.id]);
  const hasVisibleNonDataViewLayer = layers.some(
    (layer) =>
      !layer.settings[LayerSettingsKey.toPersistenceKey('DATA_VIEW_ID')] &&
      layer.settings[LayerSettingsKey.toPersistenceKey('VISIBLE')],
  );
  const hasValidVisibleDataView = view.dataViews.some(
    (dataView) =>
      dataView.hasValidBundlesConfig() &&
      viewModel.layerDefinitions[dataView.dataViewId].settings[LayerSettingsKey.toPersistenceKey('VISIBLE')],
  );
  return !(hasVisibleNonDataViewLayer || hasValidVisibleDataView);
}

function isContentEmpty(content: MenuContentElement[]) {
  if (!content) {
    return true;
  }
  const validEntries = content
    .flatMap((item) => {
      return isMenuContentElementGroup(item) ? (item as MenuItemGroup).group : [item];
    })
    .filter(Boolean)
    .filter((item) => {
      if ((item as MenuContentElement) instanceof HTMLElement) {
        return !(item as HTMLElement).hidden;
      }
      return true;
    });
  return !validEntries.length;
}

function isMenuContentElementGroup(element: MenuContentElement): element is MenuContentElement {
  return !!(element as MenuItemGroup).group;
}

export async function createMoreActionsButton(
  view: View,
  isContentView: boolean,
  writableRepositories: RepositoryDto[],
  additionalItems: () => (UpdateableElement | undefined)[],
) {
  const showMoreActions =
    isWorkbookFeatureAvailable(getStore().getState(), 'workbook-view-management') ||
    (await getExportActions(view)).length !== 0;
  if (showMoreActions) {
    const menuContent = getMenuContent(
      view,
      isTitleBarVisible(view, false),
      isContentView,
      additionalItems,
      writableRepositories,
    );
    const moreActionButton: UpdateableElement = createToolbarButton(icon(moreIcon), i18n('more-actions-menu'));
    let unByKeyMoreActionButton = setupMenuButton(moreActionButton, () => {
      return menuContent;
    });
    moreActionButton.hidden = isContentEmpty(menuContent);
    moreActionButton.update = () => {
      const content = getMenuContent(
        view,
        isTitleBarVisible(view, false),
        isContentView,
        additionalItems,
        writableRepositories,
      );
      unByKeyMoreActionButton();
      unByKeyMoreActionButton = setupMenuButton(moreActionButton, () => {
        return content;
      });
      moreActionButton.hidden = isContentEmpty(menuContent);
    };
    return moreActionButton;
  }
}

function getMenuContent(
  view: View,
  isMinified: boolean,
  isContentView: boolean,
  additionalElements: () => (UpdateableElement | undefined)[],
  writableRepositories: RepositoryDto[],
): MenuContentElement[] {
  return [
    {
      group: [
        ...additionalElements(),
        isLinearLayout() ? createVisualizationPicker(view, false) : undefined,
        !isLinearLayout() ? createMaximizeOrMinimizeMenuItem(view, isMinified) : undefined,
        createCopyViewButton(view.viewId),
        createCopyViewToWorksheetSubMenu(view.viewId),
        !isContentView ? createSaveAsCuratedMapViewMenuItem(view, writableRepositories) : undefined,
        !isContentView ? createQueryLogMenuItem(view.viewId) : undefined,
      ],
    },
    { group: [createOpenViewSettingsMenuItem(), createOpenDataManagerMenuItem(view.viewId)] },
    { group: [createRemoveViewButton(view)] },
  ];
}

function createMaximizeOrMinimizeMenuItem(view: View, isMinified: boolean) {
  if (
    isMinified &&
    !isEmbeddedContent() &&
    isWorkbookFeatureAvailable(getStore().getState(), 'workbook-view-management')
  ) {
    return view.viewId === getMaximizedViewId(getStore().getState()) ?
        createMinimizeMenuEntry()
      : createMaximizeMenuEntry(view.viewId);
  }
}

function createQueryLogMenuItem(viewId: ViewId) {
  if (isFeatureAvailable('WORKBOOK_PERFORMANCE_ANALYSIS')) {
    let tooltip;
    if (getDataViewsByViewId(getStore().getState(), viewId).some((dataView) => isDataViewOnDemand(dataView))) {
      tooltip = i18n('viewOnDemandTooltip');
    } else if (isWorksheetOnDemand(getActiveWorksheet(getStore().getState()))) {
      tooltip = i18n('worksheetOnDemandTooltip');
    }
    return {
      label: i18n('queryLog'),
      disabled: Boolean(tooltip),
      tooltip,
      execute: async () => {
        const { CreateQueryLogDialog } =
          await import('cadenza/workbook/create-query-log-dialog/create-query-log-dialog');
        return new CreateQueryLogDialog([viewId], false).connectAndShow();
      },
    };
  }
}

function createOpenViewSettingsMenuItem() {
  if (isWorkbookFeatureAvailable(getStore().getState(), 'workbook-design')) {
    return {
      label: i18n('settings'),
      execute: () => getStore().dispatch(showPanel(WorkbookPanelName.VIEW_SETTINGS)),
    };
  }
}

function createOpenDataManagerMenuItem(viewId: ViewId) {
  const viewManagementAvailable = isWorkbookFeatureAvailable(getStore().getState(), 'workbook-view-management');
  if (viewManagementAvailable && isLinearLayout() && isDetailView(getStore().getState(), viewId)) {
    return {
      label: i18n('linearLayout.dataManager'),
      execute: () => getStore().dispatch(showPanel(WorkbookPanelName.DATA_BROWSER)),
    };
  }
}

function createMaximizeMenuEntry(viewId: ViewId) {
  return {
    icon: icon(maximizeIcon),
    label: i18n('maximize'),
    execute: () => getStore().dispatch(maximizeView(viewId)),
  };
}

function createMinimizeMenuEntry() {
  return {
    icon: icon(minimizeIcon),
    label: i18n('minimize'),
    execute: () => getStore().dispatch(minimizeView()),
  };
}

export function createVisualizationPicker(view: View, asIconButton = true) {
  const isPresent =
    isWorkbookFeatureAvailable(getStore().getState(), 'workbook-view-management') &&
    !isOverviewMap(getStore().getState(), view.viewId) &&
    view.type !== ViewType.CONTROLLER &&
    view.type !== ViewType.WEBPAGE &&
    view.dataViews.length;
  if (isPresent) {
    const button = createVisualizationPickerButton(view, asIconButton);
    button.update();
    return button;
  }
}

export function createViewFiltersToolbarButton(view: View, asIconButton = true) {
  const viewFiltersActive = isWorkbookFeatureAvailable(getStore().getState(), 'workbook-data-view-analysis-context');
  if (viewFiltersActive) {
    return createViewFiltersButton(view, asIconButton);
  }
}

export function createSeparatedButtonGroup(buttons: (HTMLElement | undefined)[], groupClass?: string): HTMLElement[] {
  const buttonsToShow = buttons.filter(Boolean) as HTMLElement[];
  if (groupClass) {
    buttonsToShow.forEach((btn) => addStyleClass(btn!, groupClass));
  }
  if (buttonsToShow?.length) {
    return [
      vr({
        styleClass: [`${VIEW_ACTIONS_TOOLBAR_COMPONENT_NAME}--vertical-separator`, ...(groupClass ? [groupClass] : [])],
      }),
      ...buttonsToShow,
    ];
  }
  return [];
}

function createCopyViewButton(viewId: ViewId) {
  if (isWorkbookFeatureAvailable(getStore().getState(), 'workbook-view-management')) {
    return {
      icon: icon(duplicateViewIcon),
      label: i18n('copy'),
      execute: () => {
        const worksheetId = getActiveWorksheetId(getStore().getState());
        copyViewToWorksheet(viewId, worksheetId);
        getStore().dispatch(minimizeView());
      },
    };
  }
}

function createCopyViewToWorksheetSubMenu(viewId: ViewId) {
  if (!isWorkbookFeatureAvailable(getStore().getState(), 'workbook-view-management')) {
    return;
  }
  // clone view to an existing worksheet
  const worksheets = getWorksheets(getStore().getState());
  const entries: MenuContentElement[] = worksheets
    .filter((worksheet) => !worksheet.views.includes(viewId))
    .map((worksheet) => ({
      label: worksheet.printName,
      execute: () => {
        copyViewToWorksheetAndNavigate(viewId, worksheet.id);
      },
    }));

  // clone view to new worksheet
  entries.unshift(
    {
      label: i18n('copyToNewWorksheet'),
      execute: () => {
        const worksheetName = i18nWorksheetDialog('add-worksheet.name.default', { index: worksheets.length + 1 });
        addWorksheet(worksheetName)
          .then((newWorksheetId) => {
            copyViewToWorksheetAndNavigate(viewId, newWorksheetId);
          })
          .catch((_) => {
            // already logged
          });
      },
    },
    MENU_ITEM_SEPARATOR,
  );

  return {
    label: i18n('copyToWorksheet'),
    subMenu: entries,
  };
}

function copyViewToWorksheetAndNavigate(viewId: ViewId, worksheetId: WorksheetId) {
  copyViewToWorksheet(viewId, worksheetId)
    .then(() => {
      cadenzaSessionStorage.setItem(USER_MESSAGE_STORAGE_KEY, i18n('copyToWorksheet.success'));
      const repositoryName = getWorkbookRepositoryName(getStore().getState());
      const workbookNameAndHash = getWorkbookNameAndHash(getStore().getState());
      navigateWithinWorkbook(
        cadenzaUrl(
          createWorkbookUrl(repositoryName, workbookNameAndHash, {
            worksheetId,
          }),
        ),
      );
    })
    .catch((error) => logger.error(error));
}

function createRemoveViewButton(view: View) {
  if (isWorkbookFeatureAvailable(getStore().getState(), 'workbook-view-management')) {
    return {
      icon: icon(removeViewIcon),
      label: i18n('remove'),
      execute: () => confirmRemoveView(view),
    };
  }
}

function confirmRemoveView(view: View) {
  const connectedViews = getConnectedViewNames(view);
  if (!connectedViews.length) {
    removeView(view);
  } else {
    const viewElements = connectedViews.map((s) => h('li', s));
    const content = h('.d-stack-v.space-3', [
      i18n('confirm.connectedViews.message'),
      h(`.d-stack-v.space-1`, [i18n('confirm.connectedViews.list'), h('ul', viewElements)]),
    ]);
    confirm(i18n('confirm.connectedViews.title'), content, {
      type: 'warning',
      submitCallback: () => removeView(view),
    });
  }
}

function removeView(view: View) {
  view.remove();
  getStore().dispatch(executeCommand(Command.REMOVE_VIEW({ viewId: view.viewId })));
}

function getConnectedViewNames(view: View) {
  const otherViews = getViews(getStore().getState()).filter((v) => v.id !== view.viewId);
  const connectedViews = otherViews.filter((v) => {
    const connectedDataViews = v.dataViews.filter((dataViewId) => {
      return getDataViewConnections(getStore().getState(), dataViewId).some(
        (connection) =>
          connection.connectionType === 'DATA_EXPORT' && view.dataViewIds.includes(connection.targetDataViewId),
      );
    });
    return Boolean(connectedDataViews.length);
  });
  return connectedViews.map((v) => v.name);
}

export function createDragHandleButton() {
  if (!isEmbeddedContent() && isWorkbookFeatureAvailable(getStore().getState(), 'workbook-layout-management')) {
    return createToolbarButton(icon(moveIcon), i18n('dragHandle'), {
      styleClass: getDraggableOrNonDraggableClass(),
    });
  }
}

export function createViewInfoButton(viewId: ViewId, styleClass?: string) {
  const button = createIconButton(icon(infoIcon), i18n('info'), {
    styleClass,
    variant: 'borderless',
    size: 's',
  });
  button.hidden = !shouldShowViewInfo(viewId);
  setupPopupButton(button, (_, popup) => {
    // Setup for screen readers to read changes of the popup contents.
    popup.setAttribute('aria-live', 'polite');
    // Set the popup contents in the next run of the event queue, so that it's picked up by screen readers.
    return timeout().then(() => new ViewInfo(viewId));
  });
  (button as UpdateableElement).update = () => {
    button.hidden = !shouldShowViewInfo(viewId);
  };
  return button;
}

export function createEnlargeButton(view: View) {
  const maximize = icon(maximizeIcon);
  const minimize = icon(minimizeIcon);
  const textContainer = h('span');

  let lastScrollPosition = 0;
  const isMaximized = isDetailView(getStore().getState(), view.viewId);
  let currentIcon = isMaximized ? minimize : maximize;
  textContainer.textContent = isMaximized ? i18n('linearLayout.minimize') : i18n('linearLayout.maximize');
  const button = createButton([currentIcon, textContainer], {
    variant: 'primary',
    onclick: () => {
      closeAllPopups();
      const switchToLinearLayout = view.classList.contains('is-detail-view');
      if (switchToLinearLayout) {
        getStore().dispatch(resetDetailView());
        view.classList.remove('is-maximized');
        document.documentElement.scrollTop = lastScrollPosition;
      } else {
        lastScrollPosition = document.documentElement.scrollTop;
        getStore().dispatch(setDetailView(view.viewId));
        view.classList.add('is-maximized');
      }
      adjustToggleSizeButtonAppearance(button, textContainer, switchToLinearLayout, currentIcon);
      currentIcon = button.querySelector<Icon>('.d-icon')!;
    },
    size: 's',
  });
  adjustToggleSizeButtonAppearance(button, textContainer, isMaximized, currentIcon);
  return button;
}

/**
 * Toggles the appearance of the button either to a primary button with icon and text (enlarge/maximize)
 * - which is used in linear layout - or to a borderless button with just an icon (reset size) - which is
 * used when the view is in (enlarged) detail view.
 * @param button - The toolbar button responsible to toggle the size of the view.
 * @param textContainer - The container containing the text of the button.
 * @param isMaximized - If true, the button will appear as a primary button with icon and text.
 */
function adjustToggleSizeButtonAppearance(
  button: HTMLButtonElement,
  textContainer: HTMLElement,
  isMaximized: boolean,
  currentIcon: Icon,
) {
  textContainer.textContent = isMaximized ? i18n('linearLayout.minimize') : i18n('linearLayout.maximize');
  const label = i18n(`linearLayout.${isMaximized ? 'minimize' : 'maximize'}`);
  button.title = label;
  currentIcon.replaceWith(isMaximized ? icon(minimizeIcon) : icon(maximizeIcon));
  button.setAttribute('aria-label', label);
}

function shouldShowViewInfo(viewId: ViewId) {
  const state = getStore().getState();
  const view = getViewById(state, viewId);

  if (view.type === 'MAP' || isCombinationChart(state, viewId)) {
    return getSetting(view, CommonViewSettingsKey.toPersistenceKey('INFO_WINDOW'));
  }
  const showViewDescription = shouldShowViewDescription(view);
  const showAttributesSummary = shouldShowAttributeSummary(view);
  return showAttributesSummary || showViewDescription;
}
